import { useEffect, useRef, useState } from "react";

const EMAIL_TOKENS = [
  { name: "bg", cssVar: "--color-bg" },
  { name: "surface", cssVar: "--color-surface" },
  { name: "elevated", cssVar: "--color-elevated" },
  { name: "border", cssVar: "--color-border" },
  { name: "text-hi", cssVar: "--color-text-hi" },
  { name: "text-md", cssVar: "--color-text-md" },
  { name: "text-lo", cssVar: "--color-text-lo" },
  { name: "accent", cssVar: "--color-accent" },
] as const;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-title font-light text-text-hi mb-8">
      {children}
    </h2>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-body text-label uppercase tracking-label text-text-lo">
      {children}
    </p>
  );
}

function HexChip({
  name,
  cssVar,
  scopeRef,
}: {
  name: string;
  cssVar: string;
  scopeRef: React.RefObject<HTMLElement | null>;
}) {
  const [hex, setHex] = useState("");
  useEffect(() => {
    const el = scopeRef.current;
    if (!el) return;
    const value = getComputedStyle(el).getPropertyValue(cssVar).trim();
    setHex(value || "—");
  }, [scopeRef, cssVar]);
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className="h-6 w-10 rounded-sm border border-border"
        style={{ background: `var(${cssVar})` }}
        aria-hidden
      />
      <span className="font-mono text-label text-text-md">{name}</span>
      <span
        data-testid={`hex-${cssVar}`}
        className="font-mono text-label uppercase text-text-lo"
      >
        {hex}
      </span>
    </div>
  );
}

function LightCanvasPreview({ iframeSrc }: { iframeSrc: string }) {
  const scopeRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={scopeRef}
      data-theme="light"
      className="flex flex-col gap-4 rounded-lg border border-border bg-bg p-4 text-text-hi"
    >
      <div className="flex items-center justify-between">
        <Label>Light canvas (only)</Label>
        <span className="font-mono text-label text-text-lo">
          data-theme=&quot;light&quot;
        </span>
      </div>
      <iframe
        src={iframeSrc}
        title="Email brand kit preview"
        className="h-[900px] w-full rounded-md border border-border bg-white"
      />
      <div className="flex flex-wrap gap-4">
        {EMAIL_TOKENS.map((t) => (
          <HexChip
            key={t.name}
            name={t.name}
            cssVar={t.cssVar}
            scopeRef={scopeRef}
          />
        ))}
      </div>
    </div>
  );
}

export default function BrandEmailPage() {
  return (
    <div className="min-h-screen bg-bg text-text-hi font-body">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <section className="mb-16 flex flex-col gap-4">
          <p className="font-mono text-label uppercase tracking-label text-accent">
            Email brand kit · Living preview
          </p>
          <h1 className="font-display text-display font-light tracking-display text-text-hi">
            Email brand kit
          </h1>
          <p className="font-body text-heading text-text-md max-w-3xl">
            Render of the canonical email partial on its single standard canvas:
            light. Every Adva email ships on the light canvas — clients in
            dark-mode UIs (Apple Mail Dark, Outlook dark) render the email on
            its light background, matching Linear and GitHub practice. The
            preview uses inlined base64 logos, so it renders identically to any
            real inbox that doesn&apos;t strip data URIs. Token chips read from{" "}
            <code className="font-mono text-accent">@adva/tokens</code> so you
            can confirm the hex values match the inline CSS in the HTML.
          </p>
        </section>

        <section className="mb-16">
          <SectionTitle>Canvas preview</SectionTitle>
          <LightCanvasPreview iframeSrc="/brand/email/header-footer.html" />
        </section>

        <footer className="mt-16 flex flex-col gap-2 border-t border-border pt-8">
          <p className="font-mono text-label uppercase tracking-label text-text-lo">
            Source · packages/brand-assets/adva/email/header-footer.html
          </p>
          <p className="font-body text-body text-text-md">
            The canonical reference HTML lives in{" "}
            <code className="font-mono text-accent">
              packages/brand-assets/adva/email/
            </code>
            . A mirror at{" "}
            <code className="font-mono text-accent">
              apps/web/public/brand/email/header-footer.html
            </code>{" "}
            (generated by{" "}
            <code className="font-mono text-accent">
              scripts/sync-brand-public.ts
            </code>
            ) makes the file reachable over HTTP for this iframe — identical to
            the canonical source, inline base64 and all.
          </p>
        </footer>
      </div>
    </div>
  );
}
